import * as Yup from "yup";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import { Box, Grid, MenuItem } from "@mui/material";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import getAxiosError from "../../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { STATES_LIST } from "../../../fdp/FdpConstants";
import { AddStorePkgRequest } from "../../../../app/models/requestModels/storePkgRequest";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";
import { addStorePackage } from "../../../../app/features/dedicatedReceiving/storeManifestSlice";
import { StoreManifestData } from "../../../../app/models/responseModels/StoreManifestResponse";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import SubmitModalWithEditWithFormik from "../../../../components/common/modalPopup/SubmitModalWithEditWithFormik";

interface EditStorePackageModalProps {
  closeEditPackageModal: CallableFunction;
  packageRowData: StoreManifestData;
  onCompleted: CallableFunction;
}
function AddStorePkgFormModel(
  toteId: string,
  orderId: string,
  storePrimaryId: number,
  dropNumber: string,
  poNumber: string,
  driverUserId: string,
  displayTripId: string,
  customerAddressLine1: string,
  customerCity: string,
  customerState: string,
  customerPostalcode: string,
  scheduledDeliveryDate: Date | undefined
): AddStorePkgRequest {
  return {
    toteId: toteId.trim(),
    orderId: orderId.trim(),
    storePrimaryId: storePrimaryId,
    dropNumber: dropNumber.trim(),
    poNumber: poNumber.trim(),
    driverUserId: driverUserId.trim(),
    displayTripId: displayTripId.trim(),
    customerAddressLine1: customerAddressLine1.trim(),
    customerCity: customerCity.trim(),
    customerState: customerState.trim(),
    customerPostalcode: customerPostalcode.trim(),
    scheduledDeliveryDate: scheduledDeliveryDate,
  };
}

const EditStorePacakgeModal = ({
  closeEditPackageModal,
  onCompleted,
  packageRowData,
}: EditStorePackageModalProps) => {
  const dispatch = useAppDispatch();
  const [isEditable, setEditableStatus] = useState(false);
  const formikRef = useRef<any>(null);

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );
  const initialValues: any = {
    toteId: packageRowData.toteId,
    orderId: packageRowData.orderId,
    dropNumber: packageRowData.dropNumber.toLocaleString(),
    sid: packageRowData.sid,
    poNumber: packageRowData.poNumber,
    storePrimaryId: packageRowData.id,
    storeNumber: packageRowData.storeNumber,
    fdpName: packageRowData.fdpName,
    driverUserId: packageRowData.driverUserId,
    displayTripId: packageRowData.displayTripId,
    customerAddressLine1: packageRowData.customerAddressLine1,
    customerCity: packageRowData.customerCity,
    customerState: packageRowData.customerState,
    customerPostalcode: packageRowData.customerPostalcode,
    scheduledDeliveryDate: new Date(packageRowData.scheduledDeliveryDate),
  };

  useEffect(() => {
    if (!isEditable) {
      if (formikRef.current) {
        formikRef.current.setValues(initialValues);
      }
    }
  }, [isEditable]);

  const validationSchema = Yup.object().shape({
    toteId: Yup.string().trim().required("Tote ID is required"),
    orderId: Yup.string().trim().required("Order ID is required"),
    dropNumber: Yup.string().trim().required("Drop Number is required"),
    poNumber: Yup.string().trim().required("PO Number is required"),
    driverUserId: Yup.string().trim().required("Driver User ID is required"),
    displayTripId: Yup.string().trim().required("Display Trip ID is required"),
    customerAddressLine1: Yup.string()
      .trim()
      .required("Customer Address Line 1 is required"),
    customerCity: Yup.string().trim().required("Customer City is required"),
    customerState: Yup.string().trim().required("Customer State is required"),
    customerPostalcode: Yup.string()
      .trim()
      .matches(/^\d{5}$/, "Invalid Customer Postalcode.")
      .required("Customer Postalcode is required"),
    scheduledDeliveryDate: Yup.date()
      .required("Delivery Date is required")
      .typeError("Invalid Date"),
  });

  // ***** Handlers *****
  const handleSubmit = async (values: AddStorePkgRequest) => {
    try {
      const addStorePkgDetails = AddStorePkgFormModel(
        values.toteId,
        values.orderId,
        values.storePrimaryId,
        values.dropNumber,
        values.poNumber,
        values.driverUserId,
        values.displayTripId,
        values.customerAddressLine1,
        values.customerCity,
        values.customerState,
        values.customerPostalcode,
        values.scheduledDeliveryDate
      );
      const response = await dispatch(
        addStorePackage(addStorePkgDetails)
      ).unwrap();
      if (response.isSuccess) {
        onCompleted();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error("Package couldn't be saved.", {
        toastId: "addNewStorePackageException",
      });
    }
    return true;
  };
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={(values: any, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(values);
        setSubmitting(false);
      }}
    >
      <SubmitModalWithEditWithFormik
        title={isEditable ? "Edit Package" : "Package View"}
        allowSubmit={false}
        size="lg"
        isLoading={false}
        submitButtonName="Update"
        closeModalAfterSubmit={true}
        closeSubmitModal={closeEditPackageModal}
        isEditable={isEditable}
        setEditable={setEditableStatus}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Form>
            <Grid container columnSpacing={2}>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="driverUserId"
                  label="Driver User ID"
                  name="driverUserId"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="toteId"
                  label="Tote ID"
                  name="toteId"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="orderId"
                  label="Order ID"
                  name="orderId"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="dropNumber"
                  label="Drop Number"
                  name="dropNumber"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="sid"
                  label="SID"
                  name="sid"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="poNumber"
                  label="PO Number"
                  name="poNumber"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="displayTripId"
                  label="Display Trip ID"
                  name="displayTripId"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="displayTripId"
                  label="Display Trip ID"
                  name="displayTripId"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="storeNumber"
                  label="Store Number"
                  name="storeNumber"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="fdpName"
                  label="FDP"
                  name="fdpName"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="customerCity"
                  label="Customer City"
                  name="customerCity"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  select
                  margin="normal"
                  id="customerState"
                  label="Customer State"
                  name="customerState"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  SelectProps={{
                    MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                  }}
                  required
                >
                  {STATES_LIST.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <FormikTextField
                  margin="normal"
                  id="customerPostalcode"
                  label="Customer Postalcode"
                  name="customerPostalcode"
                  size="small"
                  fullWidth
                  variant={isEditable ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} mt={1}>
                <FormikDatePicker
                  name="scheduledDeliveryDate"
                  label="Scheduled Delivery Date"
                  variant={isEditable ? "outlined" : "filled"}
                  required
                />
              </Grid>
            </Grid>
          </Form>
        </Box>
      </SubmitModalWithEditWithFormik>
    </Formik>
  );
};

export default EditStorePacakgeModal;
